import { useEffect, useRef, type ReactNode } from "react"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"

interface AppDialogProps {
  /** Controls dialog visibility */
  open: boolean
  /** Called when dialog should close (backdrop click, Escape key, close button) */
  onClose: () => void
  /** Dialog content */
  children: ReactNode
  /** Maximum width class (default: max-w-md) */
  maxWidth?: string
  /** Additional classes for the content panel */
  className?: string
}

/**
 * Consistent modal dialog wrapper with:
 * - Backdrop blur overlay
 * - Escape key + backdrop click to close
 * - Body scroll lock when open
 * - Entrance animation (fade + zoom)
 * - Token-based shadow
 */
export function AppDialog({
  open,
  onClose,
  children,
  maxWidth = "max-w-md",
  className,
}: AppDialogProps) {
  const contentRef = useRef<HTMLDivElement>(null)

  // Handle escape key
  useEffect(() => {
    if (!open) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [open, onClose])

  // Prevent body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [open])

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
      onClose()
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/65 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        ref={contentRef}
        className={cn(
          "relative w-full rounded-lg border border-border/50 bg-card p-6 pr-10 shadow-lg mx-4",
          "animate-in fade-in-0 zoom-in-95 duration-150",
          maxWidth,
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  )
}

interface AppDialogCloseProps {
  /** Called when close button is clicked */
  onClose: () => void
  /** Additional classes */
  className?: string
}

/**
 * Standard close button for dialog top-right corner.
 * Includes focus-visible ring for accessibility.
 * Hover background visible in both light and dark modes.
 */
export function AppDialogClose({ onClose, className }: AppDialogCloseProps) {
  return (
    <button
      type="button"
      onClick={onClose}
      className={cn(
        "absolute right-3 top-3 rounded-md p-1.5 text-muted-foreground transition-colors",
        "hover:bg-accent hover:text-accent-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "ring-offset-background",
        className
      )}
    >
      <X className="h-4 w-4" />
      <span className="sr-only">Close</span>
    </button>
  )
}

interface AppDialogHeaderProps {
  children: ReactNode
  className?: string
}

/**
 * Dialog header section with consistent spacing.
 * Typically contains title and optional description.
 */
export function AppDialogHeader({ children, className }: AppDialogHeaderProps) {
  return (
    <div className={cn("mb-4", className)}>
      {children}
    </div>
  )
}

interface AppDialogTitleProps {
  children: ReactNode
  className?: string
}

/**
 * Dialog title with consistent typography.
 */
export function AppDialogTitle({ children, className }: AppDialogTitleProps) {
  return (
    <h2 className={cn("text-lg font-semibold", className)}>
      {children}
    </h2>
  )
}

interface AppDialogDescriptionProps {
  children: ReactNode
  className?: string
}

/**
 * Dialog description with muted text styling.
 */
export function AppDialogDescription({ children, className }: AppDialogDescriptionProps) {
  return (
    <p className={cn("text-sm text-muted-foreground mt-1", className)}>
      {children}
    </p>
  )
}

interface AppDialogBodyProps {
  children: ReactNode
  className?: string
}

/**
 * Dialog body content area.
 */
export function AppDialogBody({ children, className }: AppDialogBodyProps) {
  return (
    <div className={cn("space-y-4", className)}>
      {children}
    </div>
  )
}

interface AppDialogFooterProps {
  children: ReactNode
  className?: string
}

/**
 * Dialog footer with right-aligned buttons.
 * Standard spacing from body content.
 */
export function AppDialogFooter({ children, className }: AppDialogFooterProps) {
  return (
    <div className={cn("mt-6 flex gap-3", className)}>
      {children}
    </div>
  )
}
